'use client'

import { LogicalOddOneOutTask } from '@/api/services/types/questionTypes'
import { Card, CardContent } from '@/components/card'
import { Button } from '@/components/ui/common/Button'
import { useTestResponse } from '@/hooks/useTestResponse'
import { cn } from '@/utils'
import { getImageUrl } from '@/utils/getImageUrl'
import { CheckCircle, XCircle } from 'lucide-react'
import * as React from 'react'

interface TaskResult {
  taskId: string
  selectedImageId: string | null
  correctImageId: string
  isCorrect: boolean
  timeSpent: number
}

type TaskStage = 'intro' | 'task' | 'result'

interface LogicalChoiceGraphicsTaskProps {
  data: LogicalOddOneOutTask
  onComplete?: (results: TaskResult[]) => void
  onStart?: () => void
  className?: string
  showExplanation?: boolean
  moduleId: string
  questionId: string
}

export function LogicalChoiceGraphicsTask({
  data,
  onComplete,
  onStart,
  className,
  showExplanation = true,
  moduleId,
  questionId
}: LogicalChoiceGraphicsTaskProps) {
  const [stage, setStage] = React.useState<TaskStage>('intro')
  const [remainingTime, setRemainingTime] = React.useState(data.timeLimitSeconds)
  const [selectedImageId, setSelectedImageId] = React.useState<string | null>(null)
  const [results, setResults] = React.useState<TaskResult[]>([])
  const [startTime, setStartTime] = React.useState(0)
  const [isCorrect, setIsCorrect] = React.useState(false)
  const { addQuestionResponse } = useTestResponse()

  const correctImage = data.images.find((img) => img.isOddOneOut)
  const correctImageId = correctImage?.id || ''

  React.useEffect(() => {
    startTask()
  }, [])

  const startTask = () => {
    if (onStart) {
      onStart()
    }
    setStage('task')
    setSelectedImageId(null)
    setResults([])
    setRemainingTime(data.timeLimitSeconds)
    setStartTime(Date.now())
  }

  const selectImage = (imageId: string) => {
    setSelectedImageId(imageId)
  }

  const submitAnswer = () => {
    if (!selectedImageId) return

    const timeSpent = (Date.now() - startTime) / 1000
    const isCorrect = selectedImageId === correctImageId

    const result: TaskResult = {
      taskId: data.id,
      selectedImageId,
      correctImageId,
      isCorrect,
      timeSpent
    }

    // Zapisz odpowiedź w kontekście
    addQuestionResponse(moduleId, {
      questionId,
      questionType: 'LOGICAL_ODD_ONE_OUT_TASK',
      logicalOddOneOutTaskAnswer: {
        selectedOddOneOutImageId: selectedImageId,
        responseTimeMillis: timeSpent * 1000 // konwersja na milisekundy
      }
    })

    setResults([...results, result])
    setIsCorrect(isCorrect)
    setStage('result')
  }

  const completeTask = () => {
    if (onComplete) {
      onComplete(results)
    }
  }

  React.useEffect(() => {
    if (stage !== 'task' || data.timeLimitSeconds <= 0 || remainingTime <= 0) return

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          if (selectedImageId) {
            submitAnswer()
          } else {
            const timeSpent = data.timeLimitSeconds
            const result: TaskResult = {
              taskId: data.id,
              selectedImageId: null,
              correctImageId,
              isCorrect: false,
              timeSpent
            }

            // Zapisz odpowiedź w kontekście dla przypadku braku wyboru
            addQuestionResponse(moduleId, {
              questionId,
              questionType: 'LOGICAL_ODD_ONE_OUT_TASK',
              logicalOddOneOutTaskAnswer: {
                selectedOddOneOutImageId: '',
                responseTimeMillis: timeSpent * 1000
              }
            })

            setResults([...results, result])
            setIsCorrect(false)
            setStage('result')
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [
    stage,
    data.timeLimitSeconds,
    remainingTime,
    selectedImageId,
    data.id,
    correctImageId,
    results,
    moduleId,
    questionId,
    addQuestionResponse
  ])

  // Formatowanie czasu
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Renderowanie ekranu wprowadzenia
  const renderIntro = () => (
    <div className="flex flex-col items-center gap-6">
      <h1 className="text-center text-2xl font-bold">Zadanie Logiczne</h1>

      <p className="max-w-lg text-center">
        W tym zadaniu zobaczysz cztery grafiki. Twoim zadaniem jest wybrać tę, która nie
        pasuje do pozostałych.
      </p>

      <p className="max-w-lg text-center">
        Przyjrzyj się uważnie wszystkim grafikom i znajdź tę, która różni się od
        pozostałych pod względem wzoru, koloru, kształtu lub innej cechy.
      </p>

      {data.timeLimitSeconds > 0 && (
        <p className="text-center text-sm text-gray-600">
          Na zadanie masz {formatTime(data.timeLimitSeconds)} czasu.
        </p>
      )}

      <Button
        className="bg-indigo-600 text-white hover:bg-indigo-700"
        onClick={startTask}
      >
        Rozpocznij zadanie
      </Button>
    </div>
  )

  // Renderowanie ekranu zadania
  const renderTask = () => (
    <div className="flex flex-col items-center gap-6">
      <h2 className="text-center text-xl font-bold">
        Wybierz grafikę, która nie pasuje do pozostałych
      </h2>

      <div className="grid w-full max-w-2xl grid-cols-2 gap-4">
        {data.images.map((image) => (
          <div className="flex flex-col items-center" key={image.id}>
            <button
              className={cn(
                'flex aspect-square w-full items-center justify-center rounded-md border-2 p-2',
                'transition-all focus:outline-none',
                selectedImageId === image.id
                  ? 'border-[#2bcaca] ring-2 ring-[#2bcaca]'
                  : 'border-gray-200 hover:border-gray-400'
              )}
              onClick={() => selectImage(image.id)}
            >
              <img
                alt={`Grafika ${image.id}`}
                className="max-h-full max-w-full object-contain"
                src={getImageUrl(image.imageFileId) || '/placeholder.svg'}
              />
            </button>
          </div>
        ))}
      </div>

      <Button
        className="mt-4 bg-[#2bcaca] hover:bg-[#2b9999] text-white"
        disabled={!selectedImageId}
        onClick={submitAnswer}
      >
        Zatwierdź odpowiedź
      </Button>
    </div>
  )

  // Renderowanie ekranu wyniku
  const renderResult = () => (
    <div className="flex flex-col items-center gap-6">
      <div className="flex items-center gap-2">
        {isCorrect ? (
          <CheckCircle className="h-8 w-8 text-green-500" />
        ) : (
          <XCircle className="h-8 w-8 text-red-500" />
        )}
        <h2 className="text-xl font-bold">
          {isCorrect ? 'Poprawna odpowiedź!' : 'Niepoprawna odpowiedź'}
        </h2>
      </div>

      {showExplanation && (
        <p className="max-w-lg text-center text-gray-600">
          {isCorrect
            ? 'Gratulacje! Wybrałeś poprawną grafikę, która różni się od pozostałych.'
            : 'Spróbuj jeszcze raz. Przyjrzyj się uważnie różnicom między grafikami.'}
        </p>
      )}

      <Button
        className="bg-[#2bcaca] hover:bg-[#2b9999] text-white "
        onClick={completeTask}
      >
        Zakończ zadanie
      </Button>
    </div>
  )

  // Renderowanie ekranu zakończenia
  // const renderComplete = () => (
  //   <div className="flex flex-col items-center gap-6">
  //     <h2 className="text-center text-xl font-bold">Zadanie zakończone</h2>
  //     <p className="text-center text-gray-600">
  //       Dziękujemy za wykonanie zadania!
  //     </p>
  //   </div>
  // )

  return (
    <div className="relative">
      {data.timeLimitSeconds > 0 && (
        <div className="absolute -top-8 right-0 text-sm font-medium text-gray-600 bg-transparent px-3 py-1 rounded-md">
          Pozostały czas: {formatTime(remainingTime)}
        </div>
      )}
      <Card className={cn('mx-auto w-full max-w-2xl', className)}>
        <CardContent className="p-6">
          {stage === 'intro' && renderIntro()}
          {stage === 'task' && renderTask()}
          {stage === 'result' && renderResult()}
          {/* {stage === 'complete' && renderComplete()} */}
        </CardContent>
      </Card>
    </div>
  )
}
